import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import CollaboratorSection from './CollaboratorSection';
import EditorSection from './EditorSection';
import EditorSettings from './EditorSettings';
import FooterSection from './FooterSection';
import GridSettings from './GridSettings';
import HeaderSection from './HeaderSection';
import Options from './Options';
import TopSection from './TopSection';
import useApi from '../../hooks/useApi';
import Utils from '../../../app/assets/js/shared/utils';
import {
  debounce,
  setStatusSaving,
  setStatusSaved,
  setStatusSaveFailed,
  getAutoSaveInterval,
  getImageUrl,
  triggerEvent,
} from '../../utils';

import { PanelWrapper, ContentSection } from './styles';

const LoopContentEditor = ({ editor, panel, fileService, flash }) => {
  const { options, useAutoSave } = editor;
  const { userToken, integrations, circle_uuid, circle_state: state, ownerId } = options;

  const { getData, loading } = useApi(userToken);
  const { putData, deleteData } = useApi(userToken);

  const [data, setData] = useState();
  const [payload, setPayload] = useState({});
  const [updatedAt, setUpdatedAt] = useState();
  const [isUpdating, setIsUpdating] = useState(true);
  const [translations, setTranslations] = useState();
  const [loopSettings, setLoopSettings] = useState({});
  const [showSaveButton, setShowSaveButton] = useState(!useAutoSave);
  const [participations, setParticipations] = useState([]);

  const isSession = state === 'session';

  useEffect(() => {
    const getLoopData = async () => {
      if (!loading) {
        const response = await getData(`/api/v3/editor/circles/${circle_uuid}`);
        if (response.success) {
          const { loop, settings, translations } = response.body;
          const grid_settings = Utils.getGridSettings(loop.grid_settings);
          setData({ ...loop, grid_settings });
          setLoopSettings(settings);
          setTranslations(translations);
          setParticipations(settings.participations || []);
          setUpdatedAt(loop.updated_at);
        }
      }
    };
    getLoopData();
  }, []);

  useEffect(() => {
    if (data) {
      setIsUpdating(false);
    }
  }, [data]);

  useEffect(() => {
    document.addEventListener('useAutoSave', handleAutoSave);
    return () => document.removeEventListener('useAutoSave', handleAutoSave);
  }, []);

  const handleAutoSave = (ev) => {
    setShowSaveButton(!ev.detail.useAutoSave);
  };

  const debouncedSave = useCallback(
    debounce(async (item) => {
      await save(item);
    }, getAutoSaveInterval()),
    [],
  );

  useEffect(() => {
    if (useAutoSave && updatedAt && !loading) {
      setStatusSaving();
      if (payload?.immediateSave && !isUpdating) {
        save({ payload, updatedAt });
      } else {
        setIsUpdating(true);
        debouncedSave({ payload, updatedAt });
      }
    }
  }, [JSON.stringify(payload)]);

  const forceSave = async () => {
    setStatusSaving();
    await save({ payload, updatedAt });
  };

  const triggerEvents = (payload) => {
    const uuid = circle_uuid;
    const { name, icon_image, grid_settings, allowHubCreation, allowNodeMovement } = payload;
    triggerEvent('loop.updated', { uuid, name, fileName: getImageUrl(fileService, icon_image, '220x220') });
    triggerEvent('allowHubCreation', allowHubCreation);
    triggerEvent('allowNodeMovement', allowNodeMovement);
    if (grid_settings) {
      const { showGrid, snapToGrid, gridSize } = grid_settings;
      triggerEvent('showGrid', showGrid);
      triggerEvent('snapToGrid', snapToGrid);
      triggerEvent('gridSize', gridSize);
    }
  };

  const save = async ({ payload, updatedAt }) => {
    const loopData = {
      ...payload,
      updated_at: updatedAt,
    };
    const response = await putData(`/api/v3/editor/circles/${circle_uuid}`, loopData);
    if (response.success) {
      setStatusSaved();
      setIsUpdating(false);
      const { updated_at, description, purpose, ...data } = response.body;
      setUpdatedAt(updated_at);
      setData(data);
      if (!useAutoSave) {
        triggerEvents(payload);
      }
    } else {
      setStatusSaveFailed(i18n.__('behavior_save_failed'));
      setIsUpdating(false);
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__('error_edit_conflict_behavior'), { ttl: 5000 });
      } else if (response.message === 'payload_too_large') {
        flash(i18n.__('error_payload_too_large'), { ttl: 20000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  const remove = async () => {
    const response = await deleteData(`/api/v3/editor/circles/${circle_uuid}`);
    if (response.success) {
      setStatusSaved();
      window.location.replace('/my-loops/');
    } else {
      setStatusSaveFailed(i18n.__('behavior_save_failed'));
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  if (loading || !translations) {
    return null;
  }

  const getSortKey = (participant) => `${participant.id !== ownerId}_${participant.User.full_name}`;
  const collaborators = participations.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));

  return (
    <PanelWrapper>
      <TopSection panel={panel} loading={loading} translations={translations} />
      <HeaderSection
        editor={editor}
        data={data}
        payload={payload}
        setPayload={setPayload}
        translations={translations}
        loading={loading}
        loopSettings={loopSettings}
      />
      <ContentSection>
        <EditorSection
          editor={editor}
          userToken={userToken}
          integrations={integrations}
          fileService={fileService}
          data={data}
          payload={payload}
          setPayload={setPayload}
          loading={loading}
          translations={translations}
          circle_uuid={circle_uuid}
          type="loop"
        />
        <Options
          options={editor.options}
          fileService={fileService}
          flash={flash}
          data={data}
          collaborators={collaborators}
          setCollaborators={setParticipations}
          translations={translations}
          loopSettings={loopSettings}
          isSession={isSession}
        />
        <CollaboratorSection
          options={options}
          fileService={fileService}
          flash={flash}
          collaborators={collaborators}
          setCollaborators={setParticipations}
          isSession={isSession}
          translations={translations}
        />
        <GridSettings
          editor={editor}
          data={data}
          payload={payload}
          setPayload={setPayload}
          translations={translations}
        />
        <EditorSettings
          editor={editor}
          data={data}
          payload={payload}
          setPayload={setPayload}
          translations={translations}
          isSession={isSession}
        />
      </ContentSection>
      <FooterSection
        options={editor.options}
        remove={remove}
        flash={flash}
        data={data}
        save={forceSave}
        translations={translations}
        showSaveButton={showSaveButton}
        isOwner={loopSettings.isOwner}
        isCollaborator={loopSettings.isCollaborator}
        isSession={isSession}
      />
    </PanelWrapper>
  );
};

export default LoopContentEditor;

LoopContentEditor.propTypes = {
  editor: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
};
